/**
 * What the inbox row and the PR sidebar say about a loop that is on: where it
 * stands, which the sidebar's auto-fix line and cubic's inbox badge read; the
 * status line the inbox shows in cubic's tooltip; and the phase the row's
 * attention dot reacts to.
 * Client-safe: no server imports.
 */
import type { AutoFixLoopStatusView } from './status';
import type { ActiveAutoFixLoopStatus, AutoFixLoopWork } from './types';

import { formatCountedNoun, joinList } from './outcome-copy';

import { AUTO_FIX_PAUSED_TITLE } from '@/lib/auto-fix-allowance/model';

/**
 * Where a loop that is on stands, most pressing first: a run fixing (review
 * findings or failing CI checks), a loop paused by the Auto Fix PR allowance,
 * a cubic review the loop will act on, waiting for the next review or CI
 * result, a failed last run, a finish that set cubic threads aside for a
 * person, or a finish with nothing left.
 */
export type AutoFixLoopActivity =
  /** `work.openCount`: every finding open for the loop when the run started, its own included. */
  | { kind: 'fixing'; work: AutoFixLoopWork }
  /** The allowance or the PR's run limit stopped the last dispatch; the PR page says why. */
  | { kind: 'paused' }
  /** cubic is reviewing; the loop acts on the result. */
  | { kind: 'queued' }
  | { kind: 'waiting' }
  | { kind: 'failed' }
  /** A finish that left cubic threads and/or failing CI checks for a person. */
  | { kind: 'threads-left'; openIssueCount: number; failingCheckCount: number }
  | { kind: 'done' };

/**
 * Where the loop stands: `working` (a run, or a review it will act on, is
 * underway), `done` once a completed review of the head left nothing for it
 * (the `done` finish in `finished.ts`), `threads-left` when that finish set
 * cubic threads aside for a person, `failed` while its last run failed,
 * `paused` while the Auto Fix PR allowance holds it.
 */
export type AutoFixLoopPhase = 'working' | 'done' | 'threads-left' | 'failed' | 'paused';

/**
 * The issues a run works on, out of every issue still open for auto-fix when it started:
 * "10 issues now, 50 queued" when it takes only some, so the rest read as coming in later runs
 * rather than skipped; "3 issues" when it takes them all or the open count is unknown. Once
 * the run has recorded batches, how many of its issues they settled follows: "17 issues, 10
 * done", whatever it decided about each. Compact mode omits the noun: "10 now, 50 queued", "3"
 * or "17, 10 done".
 */
export function formatAutoFixLoopIssueCount(
  findingCount: number,
  openCount = findingCount,
  { compact = false, processedCount = 0 }: { compact?: boolean; processedCount?: number } = {},
) {
  const formatCount = (count: number) =>
    compact ? String(count) : formatCountedNoun(count, 'issue');
  const issues =
    openCount > findingCount
      ? `${formatCount(findingCount)} now, ${openCount - findingCount} queued`
      : formatCount(findingCount);
  return processedCount > 0 ? `${issues}, ${processedCount} done` : issues;
}

export type AutoFixLoopPresentation = {
  phase: AutoFixLoopPhase;
  /** The inbox tooltip's auto-fix line. */
  description: string;
};

export function resolveAutoFixLoopActivity(params: {
  status: ActiveAutoFixLoopStatus;
  /** Findings the running iteration was given. */
  runningFindingCount: number;
  /** Findings open for the loop when the running iteration started; absent on older records. */
  runningOpenFindingCount?: number;
  /** Findings the running iteration's recorded batches settled; absent until a batch arrives. */
  runningProcessedCount?: number;
  /** Failing checks the running CI iteration was given; 0 or absent on a findings iteration. */
  runningCheckCount?: number;
  /** Whether cubic is reviewing the pull request now; the loop acts on the result. */
  cubicReviewRunning: boolean;
  /** cubic threads still open on the pull request; `undefined` until known. */
  openIssueCount: number | undefined;
  /** Failing checks of the head a `no_issues` finish left for a person; 0 or absent otherwise. */
  failingCheckCount?: number;
}): AutoFixLoopActivity {
  const {
    status,
    runningFindingCount,
    runningOpenFindingCount,
    runningProcessedCount,
    runningCheckCount,
    cubicReviewRunning,
    openIssueCount,
    failingCheckCount = 0,
  } = params;
  if (status === 'running') {
    return {
      kind: 'fixing',
      work: runningCheckCount
        ? { kind: 'ci', count: runningCheckCount }
        : {
            kind: 'findings',
            count: runningFindingCount,
            ...(runningOpenFindingCount !== undefined
              ? { openCount: runningOpenFindingCount }
              : {}),
            ...(runningProcessedCount ? { processedCount: runningProcessedCount } : {}),
          },
    };
  }
  // A review in progress would read as queued work, but a paused PR stays paused
  // until the allowance or the PR's run limit resets.
  if (status === 'paused') {
    return { kind: 'paused' };
  }
  // A completed review always dispatches the loop again, even after it
  // finished or failed, so a review in progress means more work is coming.
  if (cubicReviewRunning) {
    return { kind: 'queued' };
  }
  switch (status) {
    case 'waiting':
      return { kind: 'waiting' };
    case 'error':
      return { kind: 'failed' };
    case 'no_issues':
      // Until the open count is known, the loop's own finish is all there is to say,
      // unless that finish itself left failing checks for a person.
      return (openIssueCount ?? 0) + failingCheckCount > 0
        ? { kind: 'threads-left', openIssueCount: openIssueCount ?? 0, failingCheckCount }
        : { kind: 'done' };
    default:
      return status satisfies never;
  }
}

/**
 * Where a loop that is on stands, read from its status view, which counts the latest run's work
 * only while that run is going. The PR page's auto-fix line and the /code PR card share it.
 * `pendingTarget` is the switch's flip still in flight, or null with none, or for a surface that
 * does not own the switch.
 */
export function resolveAutoFixLoopViewActivity(params: {
  data: AutoFixLoopStatusView;
  pendingTarget: boolean | null;
  cubicReviewRunning: boolean;
  openIssueCount: number | undefined;
}): AutoFixLoopActivity {
  const { data, pendingTarget, cubicReviewRunning, openIssueCount } = params;
  // The optimistic toggle overlays `enabled` alone, so while an enable is in
  // flight the loop keeps the disabled record's status: `off`, or a stale
  // `error` with its failed iteration. Both read as waiting, which is what the
  // server answers; once it settles a real failure comes back.
  const staleError = pendingTarget === true && data.status === 'error';
  const latest = data.iterations[0];
  const running = latest?.status === 'running';
  return resolveAutoFixLoopActivity({
    status: data.status === 'off' || staleError ? 'waiting' : data.status,
    runningFindingCount: running ? latest.findingCount : 0,
    runningOpenFindingCount: running ? (latest.openFindingCount ?? undefined) : undefined,
    runningProcessedCount: running ? latest.processedFindingCount : undefined,
    runningCheckCount: running ? latest.checkCount : 0,
    cubicReviewRunning,
    openIssueCount,
    failingCheckCount: data.failingCheckCount,
  });
}

const AUTO_FIX_LOOP_PHASES: Record<AutoFixLoopActivity['kind'], AutoFixLoopPhase> = {
  'fixing': 'working',
  'paused': 'paused',
  'queued': 'working',
  'waiting': 'working',
  'failed': 'failed',
  'threads-left': 'threads-left',
  'done': 'done',
};

/** What a finish left for a person: "2 issues", "1 failing check", "2 issues and 1 failing check". */
export function describeLeftForYou(left: { openIssueCount: number; failingCheckCount: number }) {
  return joinList([
    ...(left.openIssueCount > 0 ? [formatCountedNoun(left.openIssueCount, 'issue')] : []),
    ...(left.failingCheckCount > 0
      ? [formatCountedNoun(left.failingCheckCount, 'failing check')]
      : []),
  ]);
}

/**
 * What a running iteration is doing, in the inbox's words. The PR page's auto-fix row names a CI
 * run with them too, and a findings run with its own shorter "Fixing 3 issues".
 */
export function describeAutoFixLoopWork(work: AutoFixLoopWork): string {
  return work.kind === 'ci'
    ? `Fixing ${formatCountedNoun(work.count, 'failing check')}`
    : `Validating and fixing ${formatAutoFixLoopIssueCount(work.count, work.openCount, { processedCount: work.processedCount })}`;
}

function describeAutoFixLoopActivity(activity: AutoFixLoopActivity): string {
  switch (activity.kind) {
    case 'fixing':
      return describeAutoFixLoopWork(activity.work);
    case 'paused':
      // The inbox summary carries the status alone, so the reason stays on the PR page.
      return AUTO_FIX_PAUSED_TITLE;
    case 'queued':
      return "Waiting for cubic's review";
    case 'waiting':
      return 'Waiting for the next cubic review or CI result';
    case 'failed':
      return 'The last run failed';
    case 'threads-left':
      return `${describeLeftForYou(activity)} left for you`;
    case 'done':
      return 'No issues to fix';
    default:
      return activity satisfies never;
  }
}

export function describeAutoFixLoop(activity: AutoFixLoopActivity): AutoFixLoopPresentation {
  return {
    phase: AUTO_FIX_LOOP_PHASES[activity.kind],
    description: describeAutoFixLoopActivity(activity),
  };
}
